import React, { useEffect, useRef } from 'react';
import classnames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { DicomMetadataStore, MODULE_TYPES, useSystem } from '@ohif/core';
import { useTranslation } from 'react-i18next';
import Dropzone from 'react-dropzone';
import filesToStudies from './filesToStudies';
import { extensionManager } from '../../App';
import { Button } from '@ohif/ui-next';
import { MobileWarningDialog } from '@ohif/ui-next';

const getLoadButton = (onDrop, text, isDir, isLoading, t) => {
  return (
    <Dropzone
      onDrop={onDrop}
      noDrag
      disabled={isLoading}
    >
      {({ getRootProps, getInputProps }) => (
        <div {...getRootProps()}>
          <Button
            variant="default"
            className="bg-customgreen-200 hover:bg-customgreen-200 border-customgreen-100 hover:border-customblue-50 w-full font-medium text-white transition-colors duration-200 sm:w-32 lg:w-36"
            disabled={isLoading}
            onClick={() => {}}
          >
            {isLoading ? t('Loading files') : text}
            {isDir ? (
              <input
                {...getInputProps()}
                {...({ webkitdirectory: 'true', mozdirectory: 'true' } as any)}
                style={{ display: 'none' }}
              />
            ) : (
              <input
                {...getInputProps()}
                style={{ display: 'none' }}
              />
            )}
          </Button>
        </div>
      )}
    </Dropzone>
  );
};

type LocalProps = {
  modePath: string;
};

const DICOM_FILE_EXTENSIONS = ['dcm', 'dicom', 'ima'];



const hasDicomMagicWord = async (file: File) => {
  try {
    const headerSlice = file.slice(0, 132);
    const buffer = await headerSlice.arrayBuffer();

    if (buffer.byteLength < 132) {
      return false;
    }

    const signature = String.fromCharCode(...new Uint8Array(buffer.slice(128, 132)));
    return signature === 'DICM';
  } catch {
    return false;
  }
};

const isLikelyDicomFile = async (file: File) => {
  const name = file.name?.toLowerCase() || '';
  const extension = name.includes('.') ? name.split('.').pop() : '';

  if (file.type?.toLowerCase() === 'application/dicom') {
    return true;
  }

  if (extension && DICOM_FILE_EXTENSIONS.includes(extension)) {
    return true;
  }

  return hasDicomMagicWord(file);
};

function Local({ modePath }: LocalProps) {
  const { servicesManager } = useSystem();
  const { customizationService } = servicesManager.services;
  const { t, i18n } = useTranslation('Local');
  const navigate = useNavigate();
  const dropzoneRef = useRef();
  const [dropInitiated, setDropInitiated] = React.useState(false);
  const [loadingMessage, setLoadingMessage] = React.useState('Loading files...');
  const [fileCount, setFileCount] = React.useState(0);
  const [progressPercentage, setProgressPercentage] = React.useState(0);
  const [processedFiles, setProcessedFiles] = React.useState(0);
  const [isCancelled, setIsCancelled] = React.useState(false);
  const [formatError, setFormatError] = React.useState(false);
  const cancelProcessing = useRef(false);

  const LoadingIndicatorProgress = customizationService.getCustomization(
    'ui.loadingIndicatorProgress'
  );

  // Initializing the dicom local dataSource
  const dataSourceModules = extensionManager.modules[MODULE_TYPES.DATA_SOURCE];
  const localDataSources = dataSourceModules.reduce((acc, curr) => {
    const mods = [];
    curr.module.forEach(mod => {
      if (mod.type === 'localApi') {
        mods.push(mod);
      }
    });
    return acc.concat(mods);
  }, []);

  const firstLocalDataSource = localDataSources[0];
  const dataSource = firstLocalDataSource.createDataSource({});

  const microscopyExtensionLoaded = extensionManager.registeredExtensionIds.includes(
    '@ohif/extension-dicom-microscopy'
  );

  // Language toggle function
  const toggleLanguage = () => {
    const currentLanguage = i18n.language;
    const newLanguage = currentLanguage === 'pl-PL' ? 'en-US' : 'pl-PL';
    i18n.changeLanguage(newLanguage);
  };

  // Function to cancel the processing
  const cancelFileProcessing = () => {
    cancelProcessing.current = true;
    setIsCancelled(true);
    setLoadingMessage(t('Processing cancelled'));

    // Reset state after a delay
    setTimeout(() => {
      setDropInitiated(false);
      setProgressPercentage(0);
      setProcessedFiles(0);
      setFileCount(0);
      setLoadingMessage(t('Loading files'));
      setIsCancelled(false);
    }, 1000);
  };

  // Function to process files with progress tracking (optimized)
  const processFilesWithProgress = async files => {
    setProcessedFiles(0);
    setProgressPercentage(0);
    cancelProcessing.current = false;

    const FileLoaderService = (await import('./fileLoaderService')).default;
    const BATCH_SIZE = 10; // Process 10 files at a time for optimal speed

    const processFile = async file => {
      if (cancelProcessing.current) {
        throw new Error('Processing cancelled by user');
      }

      try {
        const fileLoaderService = new FileLoaderService(file);
        const imageId = fileLoaderService.addFile(file);
        const image = await fileLoaderService.loadFile(file, imageId);
        const dicomJSONDataset = await fileLoaderService.getDataset(image, imageId);

        DicomMetadataStore.addInstance(dicomJSONDataset);
        return true;
      } catch (error) {
        if (cancelProcessing.current) {
          throw error;
        }
        console.log(
          error.name,
          ':Error when trying to load and process local file:',
          file.name,
          error.message
        );
        return false;
      }
    };

    let processedCount = 0;

    // Process files in batches
    for (let i = 0; i < files.length; i += BATCH_SIZE) {
      if (cancelProcessing.current) {
        throw new Error('Processing cancelled by user');
      }

      const batch = files.slice(i, i + BATCH_SIZE);
      setLoadingMessage(t('Processing files'));

      // Process batch in parallel
      await Promise.all(batch.map(processFile));

      processedCount += batch.length;
      setProcessedFiles(processedCount);
      setProgressPercentage(Math.round((processedCount / files.length) * 100));

      // Small delay to allow UI updates
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    return DicomMetadataStore.getStudyInstanceUIDs();
  };

  const onDrop = async acceptedFiles => {
    if (!acceptedFiles?.length) {
      return;
    }

    const dicomValidation = await Promise.all(
      acceptedFiles.map(async file => ({
        file,
        isDicom: await isLikelyDicomFile(file),
      }))
    );

    const dicomFiles = dicomValidation.filter(result => result.isDicom).map(result => result.file);

    if (dicomFiles.length === 0) {
      setFormatError(true);
      setTimeout(() => setFormatError(false), 6000);
      return;
    }

    setDropInitiated(true);
    setFileCount(dicomFiles.length);
    setProcessedFiles(0);
    setProgressPercentage(0);
    setIsCancelled(false);
    cancelProcessing.current = false;

    if (dicomFiles.length > 100) {
      setLoadingMessage(t('Processing files count', { count: dicomFiles.length }));
    } else {
      setLoadingMessage(t('Loading files'));
    }

    try {
      let studies;

      // For small numbers of files, use original fast method
      if (dicomFiles.length <= 20) {
        studies = await filesToStudies(dicomFiles);
        if (cancelProcessing.current) {
          return; // Exit if cancelled
        }
        setProgressPercentage(100);
        setProcessedFiles(dicomFiles.length);
      } else {
        // For many files, use batch processing with progress
        studies = await processFilesWithProgress(dicomFiles);
      }

      const query = new URLSearchParams();

      if (microscopyExtensionLoaded) {
        // TODO: for microscopy, we are forcing microscopy mode, which is not ideal.
        //     we should make the local drag and drop navigate to the worklist and
        //     there user can select microscopy mode
        const smStudies = studies.filter(id => {
          const study = DicomMetadataStore.getStudy(id);
          return (
            study.series.findIndex(s => s.Modality === 'SM' || s.instances[0].Modality === 'SM') >=
            0
          );
        });

        if (smStudies.length > 0) {
          smStudies.forEach(id => query.append('StudyInstanceUIDs', id));

          modePath = 'microscopy';
        }
      }

      // Todo: navigate to work list and let user select a mode
      studies.forEach(id => query.append('StudyInstanceUIDs', id));
      query.append('datasources', 'dicomlocal');

      // Use 'viewer' as default mode if modePath is empty
      const targetPath = modePath || 'viewer';
      navigate(`/${targetPath}/dicomlocal?${decodeURIComponent(query.toString())}`);
    } catch (error) {
      if (error.message === 'Processing cancelled by user') {
        console.log('File processing was cancelled by user');
      } else {
        console.error('Error loading files:', error);
      }
      setDropInitiated(false);
      setProgressPercentage(0);
      setProcessedFiles(0);
      setIsCancelled(false);
      // Optionally show error message to user
    }
  };

  // Set body style
  useEffect(() => {
    document.body.classList.add('bg-secondary-main');
    return () => {
      document.body.classList.remove('bg-secondary-main');
    };
  }, []);

  // Initialize loading message with translation
  useEffect(() => {
    if (!dropInitiated) {
      setLoadingMessage(t('Loading files'));
    }
  }, [t, dropInitiated]);

  return (
    <Dropzone
      ref={dropzoneRef}
      onDrop={onDrop}
      noClick
    >
      {({ getRootProps }) => (
        <div
          {...getRootProps()}
          style={{ width: '100%', height: '100%' }}
        >
          <MobileWarningDialog />
          {/* Language Toggle Button */}
          <div className="absolute top-4 right-4 z-10">
            <Button
              variant="outline"
              className="bg-customgreen-200 border-customblue-20 text-customblue-10 transition-colors duration-200 hover:text-white"
              onClick={toggleLanguage}
            >
              {i18n.language === 'pl-PL' ? 'EN' : 'PL'}
            </Button>
          </div>

          <div className="flex h-screen w-screen items-center justify-center px-4 sm:px-6 lg:px-8">
            <div className="bg-secondary-main mx-auto w-full max-w-md space-y-4 rounded-xl py-8 px-6 shadow-2xl sm:max-w-lg sm:space-y-6 sm:py-12 sm:px-12 lg:max-w-xl">
              <div className="flex items-center justify-center">
                <img
                  src="/assets/luxmed-diagnostyka-poziomo-24.svg"
                  alt="LUXMED Diagnostyka"
                  className="h-12 w-auto sm:h-16 lg:h-20"
                />
              </div>
              <div className="space-y-3 py-4 text-center sm:space-y-4 sm:py-6">
                {dropInitiated ? (
                  <div className="flex flex-col items-center justify-center space-y-6 pt-8 sm:pt-12">
                    <img
                      src="/assets/spinner.color.128x128.gif"
                      alt="Loading..."
                      className="h-16 w-16 sm:h-20 sm:w-20"
                    />

                    {/* Progress Bar */}
                    <div className="w-full max-w-xs sm:max-w-sm">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-customblue-30 text-sm font-medium">
                          {t('Progress')}
                        </span>
                        <span className="text-customblue-30 text-sm font-medium">
                          {progressPercentage}%
                        </span>
                      </div>

                      {/* Progress Bar Container */}
                      <div className="relative h-3 w-full overflow-hidden rounded-full bg-gray-700 shadow-inner">
                        {/* Progress Bar Fill */}
                        <div
                          className="bg-customgreen-200 relative h-full overflow-hidden rounded-full shadow-sm transition-all duration-500 ease-out"
                          style={{ width: `${progressPercentage}%` }}
                        >
                          {/* Animated moving shine effect */}
                          <div className="absolute inset-0 h-full w-1/4 -skew-x-12 transform animate-pulse bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
                        </div>
                      </div>

                      {/* File counter */}
                      <div className="text-customblue-400 mt-2 flex items-center justify-between text-xs">
                        <span>
                          {processedFiles} {t('of')} {fileCount} {t('files')}
                        </span>
                        <span>
                          {fileCount - processedFiles} {t('remaining')}
                        </span>
                      </div>
                    </div>

                    {/* Loading message */}
                    <div className="text-center">
                      <p className="text-customblue-30 text-sm font-medium sm:text-base">
                        {loadingMessage}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    <p className="text-customblue-30 pt-0 text-lg font-medium leading-relaxed sm:text-xl lg:text-2xl">
                      {t('Drag and drop DICOM title')} <br className="hidden sm:block" />
                      {t('Drag and drop DICOM subtitle')}
                    </p>
                    <p className="text-customblue-400 text-sm leading-relaxed sm:text-base">
                      {t('Privacy note')}
                      <br className="hidden sm:block" /> {t('Privacy note subtitle')}
                    </p>
                  </div>
                )}
              </div>
              {/* Inline format error */}
              {formatError && (
                <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 shrink-0 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm font-medium text-red-400">
                    {t('Unsupported File Message')}
                  </p>
                </div>
              )}
              <div className="flex flex-col justify-center gap-3 pt-4 sm:flex-row sm:gap-4 sm:pt-6">
                {dropInitiated ? (
                  // Show cancel button when processing
                  <Button
                    variant="destructive"
                    className="w-full border-red-500 bg-red-600 font-medium text-white transition-colors duration-200 hover:border-red-600 hover:bg-red-700 sm:w-32 lg:w-36"
                    onClick={cancelFileProcessing}
                  >
                    ✕ {t('Cancel')}
                  </Button>
                ) : (
                  // Show load buttons when not processing
                  <>
                    {getLoadButton(onDrop, t('Load files'), false, dropInitiated, t)}
                    {getLoadButton(onDrop, t('Load folders'), true, dropInitiated, t)}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Dropzone>
  );
}

export default Local;
